// Source Catalog — every data pipeline this dashboard knows about.
// Status is what an engineer needs to ship the layer:
//   live      — adapter wired, endpoint working, no key required (or key shipped via env)
//   ready     — adapter scaffolded; flip a switch / drop a key to go live
//   planned   — known endpoint, no adapter yet; one afternoon of work
//   research  — we know the data exists but not the public endpoint
//
// Categories drive the "Source Catalog" panel groupings.

// "stub" — adapter file exists + returns mock data; waiting for a real data
//           source / partnership before going live
export type SourceStatus = "live" | "ready" | "planned" | "research" | "stub";

export type SourceCategory =
  | "mobility"      // traffic, transit
  | "incidents"     // citizen reports, traffic events, security
  | "environment"   // weather, air, water, vegetation
  | "imagery"       // satellite, aerial, CCTV
  | "vibes"         // news, social, market context
  | "open-data";     // data.go.th and adjacent national/local portals

export interface SourceEntry {
  id: string;
  label: string;
  vendor: string;
  category: SourceCategory;
  status: SourceStatus;
  endpoint?: string;     // URL or pattern
  apiPath?: string;      // path served by our API (when live)
  keyEnv?: string;       // env var name if a key is required
  pollSeconds?: number;
  describe: string;      // 1-line, engineer-readable
  docs?: string;
}

export const SOURCE_CATALOG: SourceEntry[] = [
  // ---- Mobility ----
  {
    id: "itic-longdo",
    label: "iTIC / Longdo traffic events",
    vendor: "iTIC + Longdo Map",
    category: "mobility",
    status: "live",
    endpoint: "https://event.longdo.com/feed/json",
    apiPath: "/api/incidents/itic",
    pollSeconds: 180,
    describe: "Accidents, closures, construction, congestion events with lat/lng + severity.",
    docs: "https://map.longdo.com/products/traffic",
  },
  {
    id: "longdo-cameras",
    label: "Longdo CCTV cameras",
    vendor: "Longdo Map",
    category: "imagery",
    status: "ready",
    endpoint: "https://camera.longdo.com/feed/?command=json",
    apiPath: "/api/cctv/longdo",
    pollSeconds: 300,
    describe: "Hundreds of street CCTV with JPG/HLS streams; bbox-filtered to the Yala municipality.",
    docs: "https://camera.longdo.com",
  },
  {
    id: "google-traffic",
    label: "Google Maps traffic raster",
    vendor: "Google",
    category: "mobility",
    status: "planned",
    keyEnv: "GOOGLE_MAPS_KEY",
    describe: "Tile overlay; commercial license required. Use as a fallback comparison to iTIC + simulated heatmap.",
  },
  {
    id: "tomtom-traffic",
    label: "TomTom traffic flow",
    vendor: "TomTom",
    category: "mobility",
    status: "planned",
    keyEnv: "TOMTOM_KEY",
    describe: "Free 2,500 req/day tier; per-segment speed vs. free-flow — good replacement for simulated heatmap.",
    docs: "https://developer.tomtom.com/traffic-api",
  },
  {
    id: "here-traffic",
    label: "HERE traffic flow + incidents",
    vendor: "HERE Technologies",
    category: "mobility",
    status: "planned",
    keyEnv: "HERE_API_KEY",
    describe: "Per-link jam factor JSON; free tier 250k req/month.",
    docs: "https://developer.here.com/",
  },

  // ---- Incidents ----
  {
    id: "traffy-fondue",
    label: "Traffy Fondue citizen reports",
    vendor: "NECTEC / Traffy",
    category: "incidents",
    status: "research",
    endpoint: "https://publicapi.traffy.in.th/share/teamchadchart/search",
    apiPath: "/api/incidents/city-reports",
    pollSeconds: 300,
    describe: "Adapter is wired and Yala-scoped (keyword + bbox), but the public share endpoint serves only Bangkok's 311 tickets and ignores the keyword filter, so the feed currently yields zero Yala features. Needs a Yala Traffy team feed to go live.",
    docs: "https://www.traffy.in.th/",
  },
  {
    id: "city-reporter-v2",
    label: "City Reporter v2 (own intake)",
    vendor: "Dr Non — internal",
    category: "incidents",
    status: "planned",
    describe: "LINE/Telegram intake writing to Supabase; same shape as Traffy. Will be served at /api/incidents/internal once the client wants their own channel.",
  },

  // ---- Environment ----
  {
    id: "open-meteo",
    label: "Open-Meteo weather + forecast",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://api.open-meteo.com/v1/forecast",
    apiPath: "/api/weather",
    pollSeconds: 1800,
    describe: "No-key weather: current + 7d hourly. Used for KPI strip; also for rain prediction overlay later.",
  },
  {
    id: "open-meteo-aq",
    label: "Open-Meteo air quality grid",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality",
    apiPath: "/api/air-quality",
    pollSeconds: 900,
    describe: "US AQI + PM2.5/PM10 from Copernicus reanalysis grid. Free, no key.",
  },
  {
    id: "air4thai",
    label: "Air4Thai — PCD stations (Yala / Deep South)",
    vendor: "Pollution Control Department",
    category: "environment",
    status: "live",
    endpoint: "http://air4thai.pcd.go.th/services/getNewAQI_JSON.php",
    apiPath: "/api/air-quality/air4thai",
    pollSeconds: 1800,
    describe: "Official Thai government AQ monitors filtered to Yala province (incl. Betong). Public, no key, HTTP only (expired TLS cert). Real PM2.5 + national AQI.",
    docs: "http://air4thai.pcd.go.th/",
  },
  {
    id: "waqi",
    label: "WAQI (World AQI) tiles",
    vendor: "WAQI / EBAS",
    category: "environment",
    status: "planned",
    keyEnv: "WAQI_TOKEN",
    endpoint: "https://api.waqi.info/v2/feed/here/?token={token}",
    describe: "Crowd-sourced AQI with tile overlay; token from waqi.info.",
  },
  {
    id: "openaq",
    label: "OpenAQ historical",
    vendor: "OpenAQ",
    category: "environment",
    status: "planned",
    endpoint: "https://api.openaq.org/v2",
    describe: "Historical air-quality time series; useful for hour-of-day baselines, not realtime.",
  },
  {
    id: "tmd-weather",
    label: "TMD Thai Meteorological Dept.",
    vendor: "TMD",
    category: "environment",
    status: "planned",
    endpoint: "https://data.tmd.go.th/api/Weather3Hours/v1/",
    keyEnv: "TMD_KEY",
    describe: "Free Thai-language forecast with 3-hour granularity. Auth required (register).",
  },
  {
    id: "gistda-flood",
    label: "GISTDA flood extent",
    vendor: "GISTDA",
    category: "environment",
    status: "planned",
    endpoint: "https://flood.gistda.or.th/",
    describe: "Sentinel-1 SAR-derived flood polygons during Thai monsoon. WMS endpoint available.",
  },
  {
    id: "open-meteo-aq-trend",
    label: "Open-Meteo AQ 8h forecast (trend)",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality (hourly forecast)",
    apiPath: "/api/air-quality/trend",
    pollSeconds: 900,
    describe: "Drives the prominent AQI badge — current + next 8h US AQI + PM2.5 sparkline + advisory band.",
  },

  // ---- Imagery ----
  {
    id: "gibs-true-color",
    label: "NASA GIBS true-color (MODIS)",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_CorrectedReflectance_TrueColor/...",
    apiPath: "(client tile fetch)",
    describe: "1km true-color daily imagery; WMTS tile layer. Free, no key.",
    docs: "https://nasa-gibs.github.io/gibs-api-docs/",
  },
  {
    id: "gibs-ndvi",
    label: "NASA GIBS NDVI",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/.../MODIS_Terra_NDVI_8Day/...",
    apiPath: "(client tile fetch)",
    describe: "Vegetation index 8-day composite. Useful for tracking rubber-canopy and cropland change across Yala's districts, and the heat-island gradient around the urban core.",
  },
  {
    id: "sentinel-hub",
    label: "Sentinel Hub Process API",
    vendor: "ESA / Sinergise",
    category: "imagery",
    status: "planned",
    keyEnv: "SENTINEL_HUB_CLIENT_ID,SENTINEL_HUB_CLIENT_SECRET",
    endpoint: "https://services.sentinel-hub.com/api/v1/process",
    describe: "10m Sentinel-2 + Sentinel-1 SAR on demand. Free OAuth tier (30k PU/month).",
    docs: "https://docs.sentinel-hub.com/api/latest/",
  },
  {
    id: "planetary-computer",
    label: "Microsoft Planetary Computer STAC",
    vendor: "Microsoft",
    category: "imagery",
    status: "planned",
    endpoint: "https://planetarycomputer.microsoft.com/api/stac/v1",
    describe: "STAC catalog + signed tile tokens; great for historical Sentinel/Landsat lookups.",
  },
  {
    id: "jaxa-himawari",
    label: "JAXA Himawari real-time",
    vendor: "JAXA / JMA",
    category: "imagery",
    status: "planned",
    endpoint: "https://www.eorc.jaxa.jp/ptree/index.html",
    describe: "Geostationary cloud loop, 10-min cadence. Use for storm approach.",
  },

  // ---- Vibes ----
  {
    id: "google-news-rss",
    label: "Google News RSS",
    vendor: "Google",
    category: "vibes",
    status: "live",
    endpoint: "https://news.google.com/rss/search?q=...",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Three keyword-driven Google News queries: Yala / Deep South (EN), the four southern border provinces (TH), and a security-scoped query (เหตุระเบิด/ยิง + ชายแดนใต้).",
  },
  {
    id: "bangkok-post-rss",
    label: "Bangkok Post Thailand RSS",
    vendor: "Bangkok Post",
    category: "vibes",
    status: "live",
    endpoint: "https://www.bangkokpost.com/rss/data/thailand.xml",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Higher-trust EN national feed. Every item is scored for Yala / Deep South relevance inside the aggregator before it reaches the desk.",
  },
  {
    id: "thaipbs-rss",
    label: "Thai PBS News RSS",
    vendor: "Thai PBS",
    category: "vibes",
    status: "live",
    endpoint: "https://news.thaipbs.or.th/rss/local.xml",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Thai public-broadcaster regional desk. Carries Deep South coverage the English wires miss — the old rss/news.xml desk URL now 404s.",
  },
  {
    id: "matichon-rss",
    label: "Matichon RSS",
    vendor: "Matichon",
    category: "vibes",
    status: "live",
    endpoint: "https://www.matichon.co.th/feed",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Independent national daily with a strong southern-border desk; closest to a civic-operations wire.",
  },
  {
    id: "prachatai-rss",
    label: "Prachatai RSS",
    vendor: "Prachatai",
    category: "vibes",
    status: "live",
    endpoint: "https://prachatai.com/rss.xml",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Human-rights and conflict reporting covering Yala, Pattani and Narathiwat most closely of any feed we pull.",
  },
  {
    id: "khaosod-english-rss",
    label: "Khaosod English RSS",
    vendor: "Khaosod",
    category: "vibes",
    status: "live",
    endpoint: "https://www.khaosodenglish.com/feed/",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "English-language national wire; adds English-language velocity on the same events the Thai feeds carry.",
  },
  {
    id: "talkwalker",
    label: "Talkwalker Free Alerts",
    vendor: "Talkwalker",
    category: "vibes",
    status: "planned",
    describe: "Email-driven alerts; route via webhook into news feed.",
  },

  // ---- Global markets — FMP + FRED ----
  {
    id: "fmp-markets",
    label: "FMP — global indices, forex, commodities",
    vendor: "Financial Modeling Prep",
    category: "vibes",
    status: "live",
    endpoint: "https://financialmodelingprep.com/api/v3/quote/...",
    apiPath: "/api/markets",
    keyEnv: "FMP_API_KEY",
    pollSeconds: 600,
    describe: "Live ticks for SET Bangkok + global indices (S&P, Nikkei, Hang Seng, FTSE, DAX), THB forex pairs, and commodities (WTI, Brent, Gold, Silver). Free tier 250/day.",
    docs: "https://site.financialmodelingprep.com/developer/docs",
  },
  {
    id: "fred-macro",
    label: "FRED — US macro + Thai baht series",
    vendor: "Federal Reserve Economic Data (St. Louis Fed)",
    category: "vibes",
    status: "live",
    endpoint: "https://api.stlouisfed.org/fred/series/observations?...",
    apiPath: "/api/markets",
    keyEnv: "FRED_API_KEY",
    pollSeconds: 600,
    describe: "Daily series: DGS10 (US 10Y yield), DGS2 (2Y), VIXCLS (volatility), DEXTHUS (THB/USD), DCOILWTICO (WTI). 120k calls/day.",
    docs: "https://fred.stlouisfed.org/docs/api/fred/",
  },

  // ---- Open data — data.go.th ----
  {
    id: "data-go-th-points",
    label: "data.go.th — curated POIs (Yala)",
    vendor: "DGA + curated",
    category: "open-data",
    status: "live",
    apiPath: "/api/datago/points",
    describe: "Curated geocoded POIs across health, education, religion, government, markets and police in Yala province — sourced from data.go.th CKAN exports.",
    docs: "https://data.go.th/dataset",
  },
  {
    id: "data-go-th-datasets",
    label: "data.go.th — dataset index",
    vendor: "DGA (Digital Government Agency)",
    category: "open-data",
    status: "live",
    endpoint: "https://data.go.th/api/3/action/package_search",
    apiPath: "/api/datago/datasets",
    pollSeconds: 3600,
    describe: "Curated Yala / Deep South CKAN datasets (MoPH, MoE, DLA, DRA, RTP, PCD, EGAT dams, SBPAC, TAT, NSO, MoTS) plus live CKAN search on ยะลา.",
    docs: "https://data.go.th/dataset",
  },
  // ---- Yala-specific government dashboards ----
  {
    id: "yala-bi",
    label: "Yala Provincial BI dashboard",
    vendor: "Yala Provincial Office",
    category: "open-data",
    status: "research",
    endpoint: "https://www.yala.go.th",
    describe: "Yala province's public information + planning indicators. Behind redirect; auth flow needed to embed live.",
    docs: "https://www.yala.go.th",
  },
  {
    id: "citydata-yala",
    label: "CityData Yala — Smart City Data Platform",
    vendor: "depa Smart City Data Platform",
    category: "open-data",
    status: "research",
    endpoint: "https://www.citydata.in.th/",
    describe: "depa-hosted Smart City Data Platform. Check for a Yala public dashboard with municipal indicators downloadable as CSV.",
    docs: "https://www.citydata.in.th/",
  },
  {
    id: "yala-nso",
    label: "Yala Provincial Statistics Office",
    vendor: "NSO Thailand · Yala branch",
    category: "open-data",
    status: "research",
    endpoint: "https://yala.nso.go.th",
    describe: "Provincial statistics: population, household income, labour, rubber/agriculture, education, public health. Updated quarterly.",
    docs: "https://yala.nso.go.th",
  },
  {
    id: "yala-gov-news",
    label: "Yala provincial government news",
    vendor: "Yala Provincial Office",
    category: "vibes",
    status: "research",
    endpoint: "https://www.yala.go.th",
    describe: "Official provincial announcement page. Behind anti-bot — needs headless browser or API key to ingest.",
    docs: "https://www.yala.go.th",
  },
  {
    id: "dla-opendata",
    label: "DLA Open Data — local administration",
    vendor: "Department of Local Administration",
    category: "open-data",
    status: "research",
    endpoint: "https://opendata.dla.go.th",
    describe: "Local-government datasets nationwide: municipal budgets, staffing, project tracking, OSCC integration. CKAN-style portal.",
    docs: "https://opendata.dla.go.th",
  },
  {
    id: "nso-provincial",
    label: "NSO National Statistical Office",
    vendor: "Office of the Prime Minister",
    category: "open-data",
    status: "research",
    endpoint: "https://www.nso.go.th/nsoweb/official_stat/province/m",
    describe: "Official provincial statistics across Thailand — economy, social, environment, governance indicators.",
    docs: "https://www.nso.go.th/nsoweb/official_stat/province/m?set_lang=en",
  },
  {
    id: "aqicn-yala",
    label: "AQICN Yala station",
    vendor: "World Air Quality Index project",
    category: "environment",
    status: "live",
    keyEnv: "AQICN_TOKEN",
    endpoint: "https://aqicn.org/city/yala/",
    apiPath: "/api/air-quality/aqicn",
    describe: "Real-time AQI from Thailand's Pollution Control Department nearest station. Cross-validates and supplements Open-Meteo AQ grid. Requires AQICN_TOKEN (free at aqicn.org/data-platform/token).",
    docs: "https://aqicn.org/api/",
  },
  {
    id: "open-dev-mekong",
    label: "Open Development Thailand (Mekong)",
    vendor: "ODT / Mekong data hub",
    category: "open-data",
    status: "research",
    endpoint: "https://data.laos.opendevelopmentmekong.net/en/organization/thailand-organization",
    describe: "Cross-border spatial + sectoral datasets covering Thailand including Deep South / Yala-area land use, water, and demographics.",
    docs: "https://opendevelopmentmekong.net",
  },
  // ---- Social ----
  {
    id: "fb-yala-municipality",
    label: "Facebook · Yala City Municipality page",
    vendor: "Meta (page plugin embed)",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.facebook.com/yalacity",
    apiPath: "/api/social/facebook",
    keyEnv: "FACEBOOK_PAGE_TOKEN",
    pollSeconds: 600,
    describe: "Official municipality page. No-auth Meta iframe embed always works; set FACEBOOK_PAGE_TOKEN + FACEBOOK_PAGE_ID for richer server-side post data.",
    docs: "https://www.facebook.com/yalacity",
  },
  // ---- Deep South security ----
  {
    id: "acled-deep-south",
    label: "ACLED — Deep South conflict incidents",
    vendor: "ACLED (w/ Deep South Watch / CSCD at PSU)",
    category: "incidents",
    status: "ready",
    endpoint: "https://api.acleddata.com/acled/read",
    apiPath: "/api/security/incidents",
    keyEnv: "ACLED_API_KEY",
    pollSeconds: 21600,
    describe: "Geocoded, dated violent-conflict events (bombing/IED, shooting, armed clash, arson, raids) across Yala/Pattani/Narathiwat with actors + fatalities. Machine-readable proxy for the Deep South Incident Database. Free with a registration key; falls back to scenario incidents when no key is set.",
    docs: "https://developer.acleddata.com",
  },
  {
    id: "deep-south-legal-status",
    label: "Martial Law + Emergency Decree status",
    vendor: "Royal Gazette / ISOC Region 4 (manual)",
    category: "incidents",
    status: "live",
    apiPath: "/api/security/status",
    describe: "Static legal-status banner: Martial Law (1914 Act) + Emergency Decree (2005) active across most Yala districts since 2004; partial lift postponed Apr 2025. Verify quarterly vs the Royal Gazette.",
    docs: "https://ratchakitcha.soc.go.th",
  },
  {
    id: "deep-south-watch",
    label: "Deep South Watch — incident summaries",
    vendor: "Deep South Watch / CSCD (PSU Pattani)",
    category: "incidents",
    status: "research",
    endpoint: "https://deepsouthwatch.org/",
    apiPath: "/api/security/dsw",
    pollSeconds: 86400,
    describe: "Canonical Deep South Incident Database + periodic casualty/incident summary reports. No public API/RSS — best-effort scrape of one headline stat; reference pointer when unavailable.",
    docs: "https://deepsouthwatch.org",
  },
  {
    id: "benar-news-deep-south",
    label: "BenarNews Deep South desk (DARK)",
    vendor: "BenarNews (BBG / USAGM, defunded)",
    category: "vibes",
    status: "research",
    endpoint: "https://www.benarnews.org/thai",
    describe: "Formerly a strong Malay-language Deep South news desk. Went dark in April 2025 after US-funded BBG/USAGM funding was cut; RSS no longer updates. Disabled — not a live feed.",
    docs: "https://www.benarnews.org",
  },
  // ---- Flood / hydrology ----
  {
    id: "flooddash",
    label: "FloodDash — Bang Lang Dam + Pattani River telemetry",
    vendor: "FloodDash (sibling Mac service, real HII/RID telemetry)",
    category: "environment",
    status: "live",
    endpoint: "https://api-flood.nonarkara.org",
    apiPath: "/api/flood/gauges, /api/flood/dam, /api/flood/river-forecast, /api/flood/shelters",
    pollSeconds: 600,
    describe: "Real telemetered Bang Lang Dam storage % (RID/HII), real named Yala water-level gauges, a GloFAS-modelled Pattani River reach ('below Bang Lang Dam'), and the DDPM emergency-shelter registry (112 shelters near Yala). Primary source for flood gauges/dam status; falls back to the Open-Meteo GloFAS proxy below if unreachable.",
    docs: "https://api-flood.nonarkara.org/api/health",
  },
  {
    id: "open-meteo-flood",
    label: "Open-Meteo Flood — Pattani River discharge (fallback)",
    vendor: "Open-Meteo (GloFAS)",
    category: "environment",
    status: "live",
    endpoint: "https://flood-api.open-meteo.com/v1/flood",
    apiPath: "/api/flood/gauges",
    pollSeconds: 10800,
    describe: "GloFAS daily river-discharge (m³/s) at the Pattani River crossing + Bang Lang Dam point. Free, no key. Fallback only — used when FloodDash's own daemon is unreachable.",
    docs: "https://open-meteo.com/en/docs/flood-api",
  },
  {
    id: "airdash",
    label: "AirDash — Deep South air-danger + washout forecast",
    vendor: "AirDash (sibling Mac service)",
    category: "environment",
    status: "live",
    endpoint: "https://api-air.nonarkara.org",
    apiPath: "/api/airdash/deep-south",
    pollSeconds: 1800,
    describe: "Composite air-danger score (heat + humidity + PM2.5 + rain-relief) and rain-washout relief forecast for Pattani/Yala/Narathiwat, plus a GISTDA satellite second-opinion PM2.5 reading. Does not duplicate Air4Thai (already ingested directly).",
    docs: "https://api-air.nonarkara.org/api/health",
  },
  {
    id: "yala-flood-ops",
    label: "Yala Provincial flood-operations situation report",
    vendor: "สำนักงานจังหวัดยะลา (Yala Provincial Office) · data.go.th",
    category: "environment",
    status: "live",
    endpoint: "https://data.go.th",
    apiPath: "/api/yala/flood-ops",
    pollSeconds: 1800,
    describe: "The province's own flood-command operating picture from its data.go.th open datasets: flood-affected villages by district, real evacuee headcounts per shelter, disaster-response equipment with dispatch phone numbers, and the standing early-warning asset inventory (towers / river CCTV / telemetry). A situation report (refreshed when the province publishes, ~monthly), distinct from FloodDash's live sensor telemetry.",
    docs: "https://data.go.th/dataset/?q=%E0%B8%AD%E0%B8%B8%E0%B8%97%E0%B8%81%E0%B8%A0%E0%B8%B1%E0%B8%A2",
  },
  // ── GISTDA (Geo-Informatics & Space Technology Development Agency) ──────

  {
    id: "gistda-poi-digital-twin",
    label: "GISTDA POI Digital Twin",
    vendor: "GISTDA",
    category: "open-data",
    status: "live",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/POI_Map_Service/MapServer/0",
    apiPath: "/api/gistda/poi",
    keyEnv: "GISTDA_API_KEY",
    describe: "100K+ national POIs: government, schools, temples, hotels, restaurants, sports, hospitals. Official Thai+English names + road names + disability access. ArcGIS FeatureServer, no auth needed for read.",
    docs: "https://gistdaportal.gistda.or.th",
  },
  {
    id: "gistda-solar-lod2",
    label: "GISTDA LOD2 Solar irradiance",
    vendor: "GISTDA",
    category: "environment",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/GISDTA_BLDGLOD2_Solar_TimeSlider_Chonburi_Month/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Monthly solar irradiance (kWh/m²) per LOD2 building footprint. 12-month time-slider. GISTDA's only public LOD2-solar tilesets cover Chonburi + Laem Chabang — returns empty for the Yala bbox until a Deep-South tileset ships (graceful fallback). Fields: sun_irr, roof type, building type, height, area.",
    docs: "https://gistdaportal.gistda.or.th",
  },
  {
    id: "gistda-admin-bounds",
    label: "GISTDA Administrative Boundaries",
    vendor: "GISTDA",
    category: "open-data",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/ข้อมูลเขตการปกครอง/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Official Thai admin boundaries: province, district (อำเภอ), tambon (ตำบล), village (หมู่บ้าน). 5 ArcGIS layers. Pending correct bbox/filter to return Yala sub-units.",
  },
  {
    id: "gistda-pm25-idw",
    label: "GISTDA PM2.5 / PM10 / CO2 / NOx IDW rasters",
    vendor: "GISTDA",
    category: "environment",
    status: "ready",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/Idw_02_PM2_Index/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Inverse Distance Weighted interpolated air-quality rasters for Thailand (PM1, PM2.5, PM4, PM10, CO2, NOx). Coverage centres on NE/central Thailand — verify extent before using for Yala.",
  },
  {
    id: "gistda-waste-2611",
    label: "GISTDA Waste Disposal Sites (2,611 nationwide)",
    vendor: "GISTDA",
    category: "open-data",
    status: "ready",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/2611landfills/FeatureServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "All 2,611 registered waste disposal / landfill sites in Thailand. Fields: site name, province, owner type, management method, area. Filter by province code when wiring the Yala layer.",
  },
  {
    id: "gistda-illegal-race",
    label: "GISTDA Illegal Street Racing Hotspots",
    vendor: "GISTDA",
    category: "incidents",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/IllegalRace/FeatureServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Point data of known illegal street racing locations + incidents with date + intensity. Safety layer — mayor can target police deployment.",
  },
  {
    id: "gistda-theos2",
    label: "GISTDA THEOS-2 Satellite (50 cm resolution)",
    vendor: "GISTDA",
    category: "imagery",
    status: "ready",
    endpoint: "https://gistdaportal.gistda.or.th",
    keyEnv: "GISTDA_API_KEY",
    describe: "Thailand's own Earth observation satellite. 50 cm resolution. GISTDA_API_KEY enables tasking requests for Yala: flood monitoring, border surveillance, agricultural (rubber) mapping, disaster response. Airbus Pleiades is the commercial high-res alternative.",
    docs: "https://www.gistda.or.th/theos-2",
  },
  {
    id: "gistda-lst",
    label: "GISTDA Land Surface Temperature (CCO)",
    vendor: "GISTDA",
    category: "environment",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/LST_CCO_tif/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Land Surface Temperature raster from Landsat/THEOS — Climate Change Observatory series. Urban heat island mapping for Yala city + Deep South districts.",
  },
  {
    id: "gistda-unl",
    label: "GISTDA UNL (Thai Address Geocoding)",
    vendor: "GISTDA",
    category: "open-data",
    status: "research",
    keyEnv: "GISTDA_API_KEY",
    describe: "GISTDA's Thai address geocoding service — convert Thai addresses to lat/lng coordinates. Endpoint under investigation; API key format may differ from ArcGIS services.",
    docs: "https://www.gistda.or.th",
  },

];

export function sourcesByStatus(status: SourceStatus) {
  return SOURCE_CATALOG.filter((s) => s.status === status);
}

export function sourcesByCategory(category: SourceCategory) {
  return SOURCE_CATALOG.filter((s) => s.category === category);
}
